import React from 'react'
import {
  getWorldCup2026TeamFlag,
  isWorldCup2026League,
} from '../../constants/worldCup2026TeamFlags'
import type { NewsTournamentExtraCard, NewsTournamentExtraKind } from '../../services/newsService'
import { useTranslation } from '../../hooks/use-i18n'

const KIND_ICON: Record<NewsTournamentExtraKind, string> = {
  top_scorer: '⚽',
  future_champion_start: '🏆',
  future_champion_second: '🏆',
  constructors_champion: '🏎️',
}

const flagFor = (league: string | null, name: string): string | null =>
  isWorldCup2026League(league) ? getWorldCup2026TeamFlag(name) : null

interface Props {
  card: NewsTournamentExtraCard
}

const NewsTournamentExtraCardView: React.FC<Props> = ({ card }) => {
  const { t } = useTranslation()
  const maxVotes = card.picks.length ? card.picks[0].votes : 0
  const kindLabel = t(`news.extraKind.${card.kind}`)

  return (
    <article className="relative bg-card border border-border shadow-lg rounded-2xl p-3 sm:p-6">
      <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground">
        <span className="font-semibold text-primary">{card.leagueLabel}</span>
        <span>
          {card.dateLabel} · {card.timeLabel}
        </span>
      </div>
      <div className="h-px bg-border my-2 sm:my-3" />

      <div className="flex items-center justify-center gap-2 mb-2 sm:mb-3">
        <span className="text-2xl" aria-hidden>{KIND_ICON[card.kind]}</span>
        <p className="text-center text-sm sm:text-base font-bold text-foreground">{kindLabel}</p>
      </div>
      <p className="text-center text-xs text-muted-foreground mb-3 sm:mb-4">{card.title}</p>

      <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
        {card.picks.slice(0, 6).map((pick) => {
          const flag = flagFor(card.league, pick.name)
          return (
            <span
              key={pick.name}
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-border text-xs font-medium text-foreground"
            >
              {flag && <span className="text-base leading-none">{flag}</span>}
              <span className="truncate max-w-[140px]">{pick.name}</span>
              <span className="font-bold">{pick.votes}</span>
            </span>
          )
        })}
        <span className="text-xs text-muted-foreground ml-1">
          {t('news.total', { count: String(card.totalPredictions) })}
        </span>
      </div>

      {card.picks.length > 0 && (
        <>
          <p className="text-center text-[11px] font-semibold tracking-widest text-muted-foreground uppercase mt-3 sm:mt-6 mb-1.5 sm:mb-3">
            {t('news.extraPickDistribution')}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-3">
            {card.picks.slice(0, 9).map((pick, i) => {
              const flag = flagFor(card.league, pick.name)
              return (
                <div
                  key={pick.name}
                  className={`rounded-lg sm:rounded-xl border p-1.5 sm:p-3 text-center bg-muted/40 ${
                    i === 0 ? 'border-primary/50' : 'border-border'
                  }`}
                >
                  {flag && <div className="text-xl sm:text-2xl leading-none mb-0.5">{flag}</div>}
                  <div className="text-xs sm:text-sm font-bold text-foreground truncate">{pick.name}</div>
                  <div className="text-[10px] sm:text-xs font-semibold text-foreground mt-0.5">
                    {pick.votes}{' '}
                    <span className="font-normal text-muted-foreground">({pick.pct}%)</span>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="space-y-1 sm:space-y-1.5 mt-3 sm:mt-4">
            {card.picks.slice(0, 12).map((pick) => (
              <div key={`bar-${pick.name}`} className="flex items-center gap-2">
                <span className="w-24 sm:w-32 shrink-0 text-xs text-muted-foreground truncate">{pick.name}</span>
                <div className="flex-1 h-4 sm:h-5 rounded bg-muted/50 overflow-hidden">
                  <div
                    className="h-full rounded bg-primary"
                    style={{ width: `${maxVotes > 0 ? Math.max(2, (pick.votes / maxVotes) * 100) : 0}%` }}
                  />
                </div>
                <span className="w-8 shrink-0 text-xs font-semibold text-foreground">{pick.votes}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {card.result && (
        <div className="mt-4 text-center text-sm">
          <span className="inline-block px-3 py-1 rounded-lg bg-primary/15 text-primary font-semibold">
            {t('news.extraResult', { result: card.result })}
          </span>
        </div>
      )}
    </article>
  )
}

export default NewsTournamentExtraCardView
